// SPDX-License-Identifier: AGPL-3.0-or-later
export type AssetKind =
	| 'avatar'
	| 'guild_icon'
	| 'banner'
	| 'splash'
	| 'embed_splash'
	| 'emoji'
	| 'sticker'
	| 'attachment';
export type AssetExtension = 'png' | 'jpeg' | 'webp' | 'gif' | 'apng' | 'avif' | 'heic' | 'heif' | 'jxl';
export type CanonicalExtension = 'png' | 'jpeg' | 'webp' | 'gif' | 'apng';
export type AnimationPolicy = 'never' | 'always' | 'premium';
export interface AssetDimensionPolicy {
	min: number;
	max: number;
}
export interface AssetFormatPolicyEntry {
	upload: ReadonlyArray<AssetExtension>;
	mimes: Readonly<Record<AssetExtension, string>>;
	storeAs: ReadonlyArray<CanonicalExtension>;
	animated: AnimationPolicy;
	maxBytes: number;
	dims?: AssetDimensionPolicy;
}
const MIME_BY_EXTENSION: Readonly<Record<AssetExtension, string>> = {
	png: 'image/png',
	jpeg: 'image/jpeg',
	webp: 'image/webp',
	gif: 'image/gif',
	apng: 'image/apng',
	avif: 'image/avif',
	heic: 'image/heic',
	heif: 'image/heif',
	jxl: 'image/jxl',
};
const RASTER_FULL = Object.freeze<Array<AssetExtension>>([
	'png',
	'jpeg',
	'webp',
	'gif',
	'apng',
	'avif',
	'heic',
	'heif',
	'jxl',
]);
const RASTER_STATIC_ONLY = Object.freeze<Array<AssetExtension>>(['png', 'jpeg', 'webp', 'avif', 'heic', 'heif', 'jxl']);
const STICKER_UPLOAD = Object.freeze<Array<AssetExtension>>(['png', 'apng', 'gif', 'webp', 'avif']);
const STORE_ANIMATED = Object.freeze<Array<CanonicalExtension>>(['webp', 'apng', 'gif']);
const STORE_STATIC = Object.freeze<Array<CanonicalExtension>>(['webp', 'png', 'jpeg']);
const STORE_STICKER = Object.freeze<Array<CanonicalExtension>>(['webp', 'apng', 'gif', 'png']);
function pickMimes(exts: ReadonlyArray<AssetExtension>): Readonly<Record<AssetExtension, string>> {
	const out: Partial<Record<AssetExtension, string>> = {};
	for (const ext of exts) {
		out[ext] = MIME_BY_EXTENSION[ext];
	}
	return Object.freeze(out as Record<AssetExtension, string>);
}
export const ASSET_FORMAT_POLICY: Readonly<Record<AssetKind, AssetFormatPolicyEntry>> = Object.freeze({
	avatar: {
		upload: RASTER_FULL,
		mimes: pickMimes(RASTER_FULL),
		storeAs: STORE_ANIMATED,
		animated: 'premium',
		maxBytes: 10 * 1024 * 1024,
		dims: {min: 128, max: 1024},
	},
	guild_icon: {
		upload: RASTER_FULL,
		mimes: pickMimes(RASTER_FULL),
		storeAs: STORE_ANIMATED,
		animated: 'premium',
		maxBytes: 10 * 1024 * 1024,
		dims: {min: 128, max: 1024},
	},
	banner: {
		upload: RASTER_FULL,
		mimes: pickMimes(RASTER_FULL),
		storeAs: STORE_ANIMATED,
		animated: 'premium',
		maxBytes: 10 * 1024 * 1024,
		dims: {min: 480, max: 2400},
	},
	splash: {
		upload: RASTER_STATIC_ONLY,
		mimes: pickMimes(RASTER_STATIC_ONLY),
		storeAs: STORE_STATIC,
		animated: 'never',
		maxBytes: 10 * 1024 * 1024,
		dims: {min: 480, max: 2400},
	},
	embed_splash: {
		upload: RASTER_STATIC_ONLY,
		mimes: pickMimes(RASTER_STATIC_ONLY),
		storeAs: STORE_STATIC,
		animated: 'never',
		maxBytes: 10 * 1024 * 1024,
		dims: {min: 480, max: 2400},
	},
	emoji: {
		upload: RASTER_FULL,
		mimes: pickMimes(RASTER_FULL),
		storeAs: STORE_ANIMATED,
		animated: 'always',
		maxBytes: 512 * 1024,
		dims: {min: 32, max: 512},
	},
	sticker: {
		upload: STICKER_UPLOAD,
		mimes: pickMimes(STICKER_UPLOAD),
		storeAs: STORE_STICKER,
		animated: 'always',
		maxBytes: 512 * 1024,
		dims: {min: 128, max: 512},
	},
	attachment: {
		upload: RASTER_FULL,
		mimes: pickMimes(RASTER_FULL),
		storeAs: STORE_ANIMATED,
		animated: 'always',
		maxBytes: 500 * 1024 * 1024,
	},
});
export function getPolicy(kind: AssetKind): AssetFormatPolicyEntry {
	return ASSET_FORMAT_POLICY[kind];
}
export function getAcceptString(kind: AssetKind): string {
	const policy = ASSET_FORMAT_POLICY[kind];
	const exts = policy.upload.map((ext) => `.${ext}`);
	const mimes = policy.upload.map((ext) => policy.mimes[ext]);
	return [...exts, ...mimes].join(',');
}
export function getMimeWhitelist(kind: AssetKind): ReadonlyArray<string> {
	const policy = ASSET_FORMAT_POLICY[kind];
	return policy.upload.map((ext) => policy.mimes[ext]);
}
export function getExtensionWhitelist(kind: AssetKind): ReadonlyArray<AssetExtension> {
	return ASSET_FORMAT_POLICY[kind].upload;
}
export function isExtensionAllowed(kind: AssetKind, ext: string): boolean {
	const withoutDot = ext.toLowerCase().replace(/^\./, '');
	const normalized = (withoutDot === 'jpg' ? 'jpeg' : withoutDot) as AssetExtension;
	return ASSET_FORMAT_POLICY[kind].upload.includes(normalized);
}
export function isMimeAllowed(kind: AssetKind, mime: string): boolean {
	const normalized = mime.toLowerCase();
	const policy = ASSET_FORMAT_POLICY[kind];
	for (const ext of policy.upload) {
		if (policy.mimes[ext] === normalized) return true;
	}
	return false;
}
